import { gql, useLazyQuery } from "@apollo/client";

// GraphQL scalar type - represents a condition ID as a number
type ConditionID = number;

const FIND_TRIALS_BY_ZIPCODE_AND_CONDITIONS = gql`
  query FindTrialsByZipCodeAndConditions(
    $countryCode: CountryCode!
    $zipCode: String!
    $conditionIds: [ConditionID!]!
  ) {
    findTrialsByZipCodeAndConditions(
      countryCode: $countryCode
      zipCode: $zipCode
      conditionIds: $conditionIds
    ) {
      id
    }
  }
`;

export type Trial = {
  id: string;
};

type QueryResult = {
  findTrialsByZipCodeAndConditions: Trial[];
};

/**
 * Lazy version that doesn't execute until called
 * Useful for manual triggering based on user actions
 */
export function useLazyTrialsByZipCodeAndConditions() {
  const [lazyFetchTrialsByZipCodeAndConditions, { data, loading, error }] = useLazyQuery<QueryResult>(
    FIND_TRIALS_BY_ZIPCODE_AND_CONDITIONS,
    {
      fetchPolicy: "cache-and-network",
    }
  );

  return {
    lazyFetchTrialsByZipCodeAndConditions,
    trials: data?.findTrialsByZipCodeAndConditions || [],
    loading,
    error,
  };
}

export function useFetchTrialsByZipCodeAndConditions() {
  const { lazyFetchTrialsByZipCodeAndConditions, loading, error } = useLazyTrialsByZipCodeAndConditions();

  /**
   * Fetch trials for a specific zipcode and set of conditions
   */
  const fetchTrialIdsByZipCodeAndConditions = async (
    zipCode: string,
    conditionIds: ConditionID[] = [], // If no conditionIds are provided, all trials will be returned
  ): Promise<string[]> => {
    try {
      const result = await lazyFetchTrialsByZipCodeAndConditions({
        variables: { countryCode: 'US', zipCode, conditionIds }
      });

      if (result.error) {
        console.error('GraphQL query failed:', result.error);
        return [];
      }

      return result.data?.findTrialsByZipCodeAndConditions.map(trial => trial.id) || [];
    } catch (error) {
      console.error('Error fetching trials:', error);
      return [];
    }
  };

  return {
    fetchTrialIdsByZipCodeAndConditions,
    loading,
    error
  };
}